import React, { useEffect, useLayoutEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ConsumptionStats, type TimeRange } from 'features/dashboard/components/ConsumptionStats'
import { DashboardHeader } from 'features/dashboard/components/DashboardHeader'
import { DashboardRecruitmentStats } from 'features/dashboard/components/DashboardRecruitmentStats'
import { DashboardRecentOffersCard } from 'features/dashboard/components/DashboardRecentOffersCard'
import { TimeSavedChart, type InterviewTimeSavedHistoryPoint, type TimeSavedChartPoint } from 'features/dashboard/components/TimeSavedChart'
import { SkillsRadarChart, type CandidateScorePoint, type MatchingCategoryAverages, type RadarOfferOption } from 'features/dashboard/components/SkillsRadarChart'
import {
  getDashboardStatsCacheKey,
  getInitialManualMatchingTimeMinutes,
  readDashboardStatsCache,
  updateManualMatchingTimeInDashboardCaches,
  writeDashboardStatsCache,
  type DashboardStatsSnapshot,
} from 'features/dashboard/utils/dashboardStatsCache'
import { organizationsApi, candidatesApi, offersApi, interviewAudioApi, applicationsApi, Organization, type RecentOfferStatsItem } from 'lib/api'
import { useAuth } from 'contexts/AuthContext'
import type { ActivityItem } from 'features/dashboard/components/RecentActivity'
import { formatDate } from 'lib/utils'

const getRangeStartDate = (range: TimeRange): Date | null => {
  const now = new Date()

  if (range === 'all') return null

  const start = new Date(now)
  if (range === 'day') {
    start.setHours(now.getHours() - 24)
    return start
  }
  if (range === 'week') {
    start.setDate(now.getDate() - 7)
    return start
  }
  if (range === 'month') {
    start.setDate(now.getDate() - 30)
    return start
  }

  start.setMonth(0, 1)
  start.setHours(0, 0, 0, 0)
  return start
}

const isInTimeRange = (dateValue: string | undefined | null, range: TimeRange) => {
  const start = getRangeStartDate(range)
  if (!start) return true
  if (!dateValue) return false

  const date = new Date(dateValue)
  return !Number.isNaN(date.getTime()) && date >= start
}

const getResults = <T,>(responseData: T[] | { results?: T[] } | undefined): T[] => {
  if (Array.isArray(responseData)) return responseData
  return responseData?.results || []
}

function readCachedSnapshot(
  selectedOrgId: number | null,
  userOrgId: number | null | undefined,
  timeRange: TimeRange,
): DashboardStatsSnapshot | null {
  return readDashboardStatsCache(getDashboardStatsCacheKey(selectedOrgId, userOrgId, timeRange))
}

function applyDashboardSnapshot(
  snapshot: DashboardStatsSnapshot,
  setters: {
    setAnalyzedCvCount: (value: number) => void
    setOffersCount: (value: number) => void
    setMatchingCount: (value: number) => void
    setManualMatchingTimeMinutes: (value: number) => void
    setTimeSavedMinutes: (value: number) => void
    setTimeSavedChartData: (value: Omit<TimeSavedChartPoint, 'interviewMinutes'>[]) => void
    setInterviewAudioPercent: (value: number) => void
    setInterviewTimeSavedMinutes: (value: number) => void
    setHiredCandidatesCount: (value: number) => void
    setAiInterviewCount: (value: number) => void
    setRecentOffersStats: (value: RecentOfferStatsItem[]) => void
    setCategoryAverages: (value: MatchingCategoryAverages | null) => void
    setCategoryAveragesCount: (value: number) => void
  },
) {
  setters.setAnalyzedCvCount(snapshot.analyzedCvCount)
  setters.setOffersCount(snapshot.offersCount)
  setters.setMatchingCount(snapshot.matchingCount ?? 0)
  setters.setManualMatchingTimeMinutes(snapshot.manualMatchingTimeMinutes)
  setters.setTimeSavedMinutes(snapshot.timeSavedMinutes)
  setters.setTimeSavedChartData(snapshot.timeSavedChartData)
  setters.setInterviewAudioPercent(snapshot.interviewAudioPercent)
  setters.setInterviewTimeSavedMinutes(snapshot.interviewTimeSavedMinutes ?? 0)
  setters.setHiredCandidatesCount(snapshot.hiredCandidatesCount ?? 0)
  setters.setAiInterviewCount(snapshot.aiInterviewCount ?? 0)
  setters.setRecentOffersStats(snapshot.recentOffersStats)
  setters.setCategoryAverages(snapshot.categoryAverages)
  setters.setCategoryAveragesCount(snapshot.categoryAveragesCount)
}

export function Dashboard() {
  const { t } = useTranslation()
  const { user } = useAuth()
  const userOrgId = user?.organization
    ? (typeof user.organization === 'number' ? user.organization : user.organization.id)
    : null
  const [loading, setLoading] = useState(false)
  const [candidates, setCandidates] = useState<any[]>([])
  const [offers, setOffers] = useState<any[]>([])
  const [deletedCandidates, setDeletedCandidates] = useState<any[]>([])
  const [invalidCandidates, setInvalidCandidates] = useState<any[]>([])
  const [deletedOffers, setDeletedOffers] = useState<any[]>([])
  const [modifiedOffers, setModifiedOffers] = useState<any[]>([])
  const [organizationsList, setOrganizationsList] = useState<Organization[]>([])
  const [selectedOrgId, setSelectedOrgId] = useState<number | null>(null)
  const [orgsLoaded, setOrgsLoaded] = useState(false)
  const [timeRange, setTimeRange] = useState<TimeRange>('year')
  const initialSnapshot = readCachedSnapshot(null, userOrgId, 'year')
  const [interviewAudioPercent, setInterviewAudioPercent] = useState(initialSnapshot?.interviewAudioPercent ?? 0)
  const [interviewTimeSavedMinutes, setInterviewTimeSavedMinutes] = useState(initialSnapshot?.interviewTimeSavedMinutes ?? 0)
  const [hiredCandidatesCount, setHiredCandidatesCount] = useState(initialSnapshot?.hiredCandidatesCount ?? 0)
  const [aiInterviewCount, setAiInterviewCount] = useState(initialSnapshot?.aiInterviewCount ?? 0)
  const [analyzedCvCount, setAnalyzedCvCount] = useState(initialSnapshot?.analyzedCvCount ?? 0)
  const [offersCount, setOffersCount] = useState(initialSnapshot?.offersCount ?? 0)
  const [matchingCount, setMatchingCount] = useState(initialSnapshot?.matchingCount ?? 0)
  const [manualMatchingTimeMinutes, setManualMatchingTimeMinutes] = useState(
    getInitialManualMatchingTimeMinutes(initialSnapshot),
  )
  const [timeSavedMinutes, setTimeSavedMinutes] = useState(initialSnapshot?.timeSavedMinutes ?? 0)
  const [timeSavedChartData, setTimeSavedChartData] = useState<Omit<TimeSavedChartPoint, 'interviewMinutes'>[]>(initialSnapshot?.timeSavedChartData ?? [])
  const [interviewTimeSavedHistory, setInterviewTimeSavedHistory] = useState<InterviewTimeSavedHistoryPoint[]>([])
  const [recentOffersStats, setRecentOffersStats] = useState<RecentOfferStatsItem[]>(initialSnapshot?.recentOffersStats ?? [])
  const [categoryAverages, setCategoryAverages] = useState<MatchingCategoryAverages | null>(initialSnapshot?.categoryAverages ?? null)
  const [categoryAveragesCount, setCategoryAveragesCount] = useState(initialSnapshot?.categoryAveragesCount ?? 0)
  const [radarOffers, setRadarOffers] = useState<RadarOfferOption[]>([])
  const [selectedRadarOfferId, setSelectedRadarOfferId] = useState<number | null>(null)
  const [selectedScatterOfferId, setSelectedScatterOfferId] = useState<number | null>(null)
  const [candidateScorePoints, setCandidateScorePoints] = useState<CandidateScorePoint[]>([])
  const [candidateScoreTotal, setCandidateScoreTotal] = useState(0)

  const statsCacheKey = useMemo(
    () => (user ? getDashboardStatsCacheKey(selectedOrgId, userOrgId, timeRange) : null),
    [user, selectedOrgId, userOrgId, timeRange],
  )

  useLayoutEffect(() => {
    if (!statsCacheKey) return

    const snapshot = readDashboardStatsCache(statsCacheKey)
    if (!snapshot) return

    applyDashboardSnapshot(snapshot, {
      setAnalyzedCvCount,
      setOffersCount,
      setMatchingCount,
      setManualMatchingTimeMinutes,
      setTimeSavedMinutes,
      setTimeSavedChartData,
      setInterviewAudioPercent,
      setInterviewTimeSavedMinutes,
      setHiredCandidatesCount,
      setAiInterviewCount,
      setRecentOffersStats,
      setCategoryAverages,
      setCategoryAveragesCount,
    })
  }, [statsCacheKey])

  useEffect(() => {
    if (!statsCacheKey) return

    writeDashboardStatsCache(statsCacheKey, {
      analyzedCvCount,
      offersCount,
      matchingCount,
      manualMatchingTimeMinutes,
      timeSavedMinutes,
      timeSavedChartData,
      interviewAudioPercent,
      interviewTimeSavedMinutes,
      hiredCandidatesCount,
      aiInterviewCount,
      recentOffersStats,
      categoryAverages,
      categoryAveragesCount,
    })
  }, [
    statsCacheKey,
    analyzedCvCount,
    offersCount,
    matchingCount,
    manualMatchingTimeMinutes,
    timeSavedMinutes,
    timeSavedChartData,
    interviewAudioPercent,
    interviewTimeSavedMinutes,
    hiredCandidatesCount,
    aiInterviewCount,
    recentOffersStats,
    categoryAverages,
    categoryAveragesCount,
  ])

  useEffect(() => {
    const fetchData = async (orgId?: number) => {
      try {
        // If superuser, fetch all organizations on initial load
        if (user?.is_superuser && !orgsLoaded) {
          try {
            const orgsResponse = await organizationsApi.getAll()
            const orgsList = Array.isArray(orgsResponse.data) ? orgsResponse.data : orgsResponse.data.results || []
            setOrganizationsList(orgsList)
            setOrgsLoaded(true)
            // Keep selectedOrgId as null to show superuser's dashboard by default
          } catch (error) {
            console.error('Error fetching organizations:', error)
            setOrgsLoaded(true)
          }
        }

        // Determine which organization to fetch data for
        const dataOrgId = orgId || (user?.organization ? (typeof user.organization === 'number' ? user.organization : user.organization.id) : null)

        // For non-superusers without organization, don't proceed
        if (!dataOrgId && !user?.is_superuser) {
          setLoading(false)
          return
        }

        // Fetch regular candidates and offers - filter by organization only if not superuser or if org is specified
        const candidateParams: any = { page_size: 100, ordering: '-updated_at' }
        const offerParams: any = { page_size: 20, ordering: '-updated_at' }
        
        // Add organization filter for non-superusers or when a specific org is selected
        if ((!user?.is_superuser || orgId) && dataOrgId) {
          candidateParams.organization = dataOrgId
          offerParams.organization = dataOrgId
        }
        
        const [candidatesResponse, offersResponse] = await Promise.all([
          candidatesApi.getAll(candidateParams),
          offersApi.getAll({ ...offerParams, light: 'true' }),
        ])

        setCandidates(getResults(candidatesResponse.data))
        setOffers(getResults(offersResponse.data))

        // Fetch LLM usage details - only if we have an organization ID
        if (dataOrgId) {
          try {
            const [deleted, invalid, deletedOffs, modified] = await Promise.all([
              organizationsApi.recent_deleted_candidates(dataOrgId),
              organizationsApi.recent_invalid_candidates(dataOrgId),
              organizationsApi.recent_deleted_offers(dataOrgId),
              organizationsApi.recent_modified_offers(dataOrgId),
            ])
          
          // Fetch candidate/offer details by ID and enrich data
          const enrichedDeleted = await Promise.all(
            (deleted.data || []).map(async (item: any) => {
              try {
                const candidate = await candidatesApi.getById(item.usage_object_id)
                return { ...item, candidate_name: candidate.data.name }
              } catch (err) {
                return { ...item, candidate_name: t('dashboard.activity.deletedCandidate', 'Deleted Candidate') }
              }
            })
          )
          
          const enrichedInvalid = await Promise.all(
            (invalid.data || []).map(async (item: any) => {
              try {
                const candidate = await candidatesApi.getById(item.usage_object_id)
                return { ...item, candidate_name: candidate.data.name }
              } catch (err) {
                return { ...item, candidate_name: t('dashboard.activity.invalidCandidate', 'Invalid Candidate') }
              }
            })
          )
          
          const enrichedDeletedOffers = await Promise.all(
            (deletedOffs.data || []).map(async (item: any) => {
              try {
                const offer = await offersApi.getById(item.usage_object_id)
                return { ...item, offer_title: offer.data.job_title || offer.data.title }
              } catch (err) {
                return { ...item, offer_title: t('dashboard.activity.deletedOffer', 'Deleted Offer') }
              }
            })
          )
          
          const enrichedModified = await Promise.all(
            (modified.data || []).map(async (item: any) => {
              try {
                const offer = await offersApi.getById(item.usage_object_id)
                return { ...item, offer_title: offer.data.job_title || offer.data.title }
              } catch (err) {
                return { ...item, offer_title: t('dashboard.activity.modifiedOffer', 'Modified Offer') }
              }
            })
          )
          
          setDeletedCandidates(enrichedDeleted)
          setInvalidCandidates(enrichedInvalid)
          setDeletedOffers(enrichedDeletedOffers)
          setModifiedOffers(enrichedModified)
          } catch (error) {
            console.error('Error fetching LLM usage details:', error)
          }
        } else {
          // Clear LLM usage details if no organization
          setDeletedCandidates([])
          setInvalidCandidates([])
          setDeletedOffers([])
          setModifiedOffers([])
        }
      } catch (error) {
        console.error('Error fetching dashboard data:', error)
      } finally {
        setLoading(false)
      }
    }

    fetchData()
  }, [user, t, orgsLoaded])

  // Handle organization selection change (including switch back to Superuser)
  useEffect(() => {
    // Only run when user is superuser and organizations have been loaded
    if (!user?.is_superuser || !orgsLoaded) return

    const fetchOrgData = async () => {
      try {
        // Fetch candidates and offers - filter by organization only when specific org is selected
        const candidateParams: any = { page_size: 100, ordering: '-updated_at' }
        const offerParams: any = { page_size: 20, ordering: '-updated_at' }
        
        if (selectedOrgId) {
          candidateParams.organization = selectedOrgId
          offerParams.organization = selectedOrgId
        }
        
        const [candidatesResponse, offersResponse] = await Promise.all([
          candidatesApi.getAll(candidateParams),
          offersApi.getAll({ ...offerParams, light: 'true' }),
        ])
        setCandidates(getResults(candidatesResponse.data))
        setOffers(getResults(offersResponse.data))

          // Fetch LLM usage details only if we have a selected organization
          if (selectedOrgId) {
            try {
              const [deleted, invalid, deletedOffs, modified] = await Promise.all([
                organizationsApi.recent_deleted_candidates(selectedOrgId),
                organizationsApi.recent_invalid_candidates(selectedOrgId),
                organizationsApi.recent_deleted_offers(selectedOrgId),
                organizationsApi.recent_modified_offers(selectedOrgId),
              ])
            
            // Fetch candidate/offer details by ID and enrich data
            const enrichedDeleted = await Promise.all(
              (deleted.data || []).map(async (item: any) => {
                try {
                  const candidate = await candidatesApi.getById(item.usage_object_id)
                  return { ...item, candidate_name: candidate.data.name }
                } catch (err) {
                  return { ...item, candidate_name: t('dashboard.activity.deletedCandidate', 'Deleted Candidate') }
                }
              })
            )
            
            const enrichedInvalid = await Promise.all(
              (invalid.data || []).map(async (item: any) => {
                try {
                  const candidate = await candidatesApi.getById(item.usage_object_id)
                  return { ...item, candidate_name: candidate.data.name }
                } catch (err) {
                  return { ...item, candidate_name: t('dashboard.activity.invalidCandidate', 'Invalid Candidate') }
                }
              })
            )
            
            const enrichedDeletedOffers = await Promise.all(
              (deletedOffs.data || []).map(async (item: any) => {
                try {
                  const offer = await offersApi.getById(item.usage_object_id)
                  return { ...item, offer_title: offer.data.job_title || offer.data.title }
                } catch (err) {
                  return { ...item, offer_title: t('dashboard.activity.deletedOffer', 'Deleted Offer') }
                }
              })
            )
            
            const enrichedModified = await Promise.all(
              (modified.data || []).map(async (item: any) => {
                try {
                  const offer = await offersApi.getById(item.usage_object_id)
                  return { ...item, offer_title: offer.data.job_title || offer.data.title }
                } catch (err) {
                  return { ...item, offer_title: t('dashboard.activity.modifiedOffer', 'Modified Offer') }
                }
              })
            )
            
            setDeletedCandidates(enrichedDeleted)
            setInvalidCandidates(enrichedInvalid)
            setDeletedOffers(enrichedDeletedOffers)
            setModifiedOffers(enrichedModified)
            } catch (error) {
              console.error('Error fetching LLM usage details:', error)
            }
          } else {
            // Clear LLM usage details if no organization selected (Superuser dashboard)
            setDeletedCandidates([])
            setInvalidCandidates([])
            setDeletedOffers([])
            setModifiedOffers([])
          }
        } catch (error) {
          console.error('Error fetching organization dashboard data:', error)
        } finally {
          setLoading(false)
        }
      }

      fetchOrgData()
  }, [selectedOrgId, user?.is_superuser, orgsLoaded, t])

  useEffect(() => {
    const fetchInterviewStats = async () => {
      const dataOrgId = selectedOrgId || (user?.organization ? (typeof user.organization === 'number' ? user.organization : user.organization.id) : null)
      if (!dataOrgId && !user?.is_superuser) return

      const startDate = getRangeStartDate(timeRange)
      const params: { organization?: number, created_after?: string } = {}
      if (user?.is_superuser && selectedOrgId) {
        params.organization = selectedOrgId
      }
      if (startDate) {
        params.created_after = startDate.toISOString()
      }

      try {
        const response = await interviewAudioApi.getStats(params)
        setInterviewAudioPercent(response.data.interview_percentage || 0)
        setInterviewTimeSavedMinutes(response.data.interview_time_saved_minutes || 0)
        setAiInterviewCount(response.data.total_count || 0)
        setInterviewTimeSavedHistory(response.data.history || [])
      } catch (error) {
        console.error('Error fetching interview audio stats:', error)
        setInterviewAudioPercent(0)
        setInterviewTimeSavedMinutes(0)
        setAiInterviewCount(0)
        setInterviewTimeSavedHistory([])
      }
    }

    fetchInterviewStats()
  }, [selectedOrgId, timeRange, user])

  useEffect(() => {
    const fetchAnalyzedCvCount = async () => {
      const dataOrgId = selectedOrgId || (user?.organization ? (typeof user.organization === 'number' ? user.organization : user.organization.id) : null)
      if (!dataOrgId && !user?.is_superuser) {
        setAnalyzedCvCount(0)
        setHiredCandidatesCount(0)
        return
      }

      const startDate = getRangeStartDate(timeRange)
      const params: { organization?: number, created_after?: string } = {}
      if (user?.is_superuser && selectedOrgId) {
        params.organization = selectedOrgId
      }
      if (startDate) {
        params.created_after = startDate.toISOString()
      }

      try {
        const response = await candidatesApi.getStats(params)
        setAnalyzedCvCount(response.data.total_candidates || 0)
        setHiredCandidatesCount(response.data.hired_candidates || 0)
      } catch (error) {
        console.error('Error fetching analyzed CV count:', error)
        setAnalyzedCvCount(0)
        setHiredCandidatesCount(0)
      }
    }

    fetchAnalyzedCvCount()
  }, [selectedOrgId, timeRange, user])

  useEffect(() => {
    const fetchOffersCount = async () => {
      const dataOrgId = selectedOrgId || (user?.organization ? (typeof user.organization === 'number' ? user.organization : user.organization.id) : null)
      if (!dataOrgId && !user?.is_superuser) {
        setOffersCount(0)
        return
      }

      const startDate = getRangeStartDate(timeRange)
      const params: { organization?: number, created_after?: string } = {}
      if (user?.is_superuser && selectedOrgId) {
        params.organization = selectedOrgId
      }
      if (startDate) {
        params.created_after = startDate.toISOString()
      }

      try {
        const response = await offersApi.getStats(params)
        setOffersCount(response.data.total_offers || 0)
      } catch (error) {
        console.error('Error fetching offers count:', error)
        setOffersCount(0)
      }
    }

    fetchOffersCount()
  }, [selectedOrgId, timeRange, user])

  useEffect(() => {
    const fetchMatchingStats = async () => {
      const dataOrgId = selectedOrgId || (user?.organization ? (typeof user.organization === 'number' ? user.organization : user.organization.id) : null)
      if (!dataOrgId && !user?.is_superuser) {
        setMatchingCount(0)
        setTimeSavedMinutes(0)
        setTimeSavedChartData([])
        return
      }

      const startDate = getRangeStartDate(timeRange)
      const params: { organization?: number, created_after?: string } = {}
      if (user?.is_superuser && selectedOrgId) {
        params.organization = selectedOrgId
      }
      if (startDate) {
        params.created_after = startDate.toISOString()
      }

      try {
        const response = await applicationsApi.getMatchingStats(params)
        setMatchingCount(response.data.total_matching_jobs || 0)

        const chartData = (response.data.history || []).map((item): Omit<TimeSavedChartPoint, 'interviewMinutes'> => {
          const date = new Date(item.date)
          const formattedDate = timeRange === 'day'
            ? date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
            : formatDate(item.date)

          return {
            date: item.date,
            formattedDate,
            minutes: Number(item.time_saved_minutes || 0),
            totalCandidateCreation: Number(item.total_candidates || 0),
            candidateCreationInvalid: 0,
            totalOfferCreation: 0,
            totalOfferModification: 0,
          }
        })

        setTimeSavedChartData(chartData)
        setTimeSavedMinutes(response.data.time_saved_minutes || 0)

        if (typeof response.data.manual_matching_time_minutes === 'number' && response.data.manual_matching_time_minutes > 0) {
          setManualMatchingTimeMinutes(response.data.manual_matching_time_minutes)
          updateManualMatchingTimeInDashboardCaches(response.data.manual_matching_time_minutes)
        }
      } catch (error) {
        console.error('Error fetching matching stats:', error)
        setMatchingCount(0)
        setTimeSavedMinutes(0)
        setTimeSavedChartData([])
      }
    }

    fetchMatchingStats()
  }, [selectedOrgId, timeRange, user])

  useEffect(() => {
    const fetchRecentOffersStats = async () => {
      const dataOrgId = selectedOrgId || (user?.organization ? (typeof user.organization === 'number' ? user.organization : user.organization.id) : null)
      if (!dataOrgId && !user?.is_superuser) {
        setRecentOffersStats([])
        return
      }

      const startDate = getRangeStartDate(timeRange)
      const params: { organization?: number, limit?: number, created_after?: string } = { limit: 5 }
      if (user?.is_superuser && selectedOrgId) {
        params.organization = selectedOrgId
      }
      if (startDate) {
        params.created_after = startDate.toISOString()
      }

      try {
        const response = await applicationsApi.getRecentOffersStats(params)
        setRecentOffersStats(response.data.offers || [])
      } catch (error) {
        console.error('Error fetching recent offers stats:', error)
        setRecentOffersStats([])
      }
    }

    fetchRecentOffersStats()
  }, [selectedOrgId, timeRange, user])

  useEffect(() => {
    setSelectedRadarOfferId(null)
    setSelectedScatterOfferId(null)
  }, [selectedOrgId])

  useEffect(() => {
    const fetchRadarOffers = async () => {
      const dataOrgId = selectedOrgId || (user?.organization ? (typeof user.organization === 'number' ? user.organization : user.organization.id) : null)
      if (!dataOrgId && !user?.is_superuser) {
        setRadarOffers([])
        return
      }

      const params: { organization?: number, page_size?: number, ordering?: string, light?: string } = {
        page_size: 100,
        ordering: '-updated_at',
        light: 'true',
      }
      if (dataOrgId) {
        params.organization = dataOrgId
      }

      try {
        const response = await offersApi.getAll(params)
        const offerList = getResults(response.data)
        setRadarOffers(
          offerList.map((offer: any) => ({
            id: offer.id,
            label: offer.job_title || offer.title || t('dashboard.activity.untitledOffer', 'Untitled Offer'),
          }))
        )
      } catch (error) {
        console.error('Error fetching radar offer options:', error)
        setRadarOffers([])
      }
    }

    fetchRadarOffers()
  }, [selectedOrgId, user, t])

  useEffect(() => {
    const fetchCategoryAverages = async () => {
      const dataOrgId = selectedOrgId || (user?.organization ? (typeof user.organization === 'number' ? user.organization : user.organization.id) : null)
      if (!dataOrgId && !user?.is_superuser) {
        setCategoryAverages(null)
        setCategoryAveragesCount(0)
        return
      }

      const startDate = getRangeStartDate(timeRange)
      const params: { organization?: number, created_after?: string, offer?: number } = {}
      if (dataOrgId) {
        params.organization = dataOrgId
      }
      if (startDate) {
        params.created_after = startDate.toISOString()
      }
      if (selectedRadarOfferId) {
        params.offer = selectedRadarOfferId
      }

      try {
        const response = await applicationsApi.getMatchingCategoryAverages(params)
        setCategoryAverages(response.data.category_averages)
        setCategoryAveragesCount(response.data.total_results || 0)
      } catch (error) {
        console.error('Error fetching matching category averages:', error)
        setCategoryAverages(null)
        setCategoryAveragesCount(0)
      }
    }

    fetchCategoryAverages()
  }, [selectedOrgId, timeRange, user, selectedRadarOfferId])

  useEffect(() => {
    const fetchCandidateScoreDistribution = async () => {
      const dataOrgId = selectedOrgId || (user?.organization ? (typeof user.organization === 'number' ? user.organization : user.organization.id) : null)
      if (!dataOrgId && !user?.is_superuser) {
        setCandidateScorePoints([])
        setCandidateScoreTotal(0)
        return
      }

      const startDate = getRangeStartDate(timeRange)
      const params: { organization?: number, created_after?: string, offer?: number } = {}
      if (dataOrgId) {
        params.organization = dataOrgId
      }
      if (startDate) {
        params.created_after = startDate.toISOString()
      }
      if (selectedScatterOfferId) {
        params.offer = selectedScatterOfferId
      }

      try {
        const response = await applicationsApi.getCandidateScoreDistribution(params)
        setCandidateScorePoints(response.data.points || [])
        setCandidateScoreTotal(response.data.total_candidates || 0)
      } catch (error) {
        console.error('Error fetching candidate score distribution:', error)
        setCandidateScorePoints([])
        setCandidateScoreTotal(0)
      }
    }

    fetchCandidateScoreDistribution()
  }, [selectedOrgId, timeRange, user, selectedScatterOfferId])

  const candidateActivities: ActivityItem[] = useMemo(() => {
    // Helper function to safely parse dates
    const getDate = (dateStr: string | undefined | null): Date | null => {
      if (!dateStr) return null
      try {
        const date = new Date(dateStr)
        return isNaN(date.getTime()) ? null : date
      } catch {
        return null
      }
    }

    // Regular candidate activities
    const regularActivities: ActivityItem[] = candidates
      .filter(candidate => candidate.created_at)
      .slice(0, 10)
      .map(candidate => {
        const statusLabel = t(`status.${candidate.status}`, candidate.status)
        return {
          id: `candidate-${candidate.id}`,
          type: 'candidate_updated',
          description: t('dashboard.activity.candidateStatusUpdated', {
            name: candidate.name,
            status: statusLabel,
          }),
          timestamp: candidate.updated_at || candidate.created_at || new Date().toISOString(),
        }
      })

    // LLM usage history activities - deleted candidates
    const deletedCandidateActivities: ActivityItem[] = deletedCandidates.map((item: any, idx: number) => ({
      id: `deleted-candidate-${item.id || idx}`,
      type: 'candidate_updated',
      description: t('dashboard.activity.candidateDeleted', {
        name: item.candidate_name || t('dashboard.activity.deletedCandidate', 'Deleted Candidate'),
        defaultValue: `Deleted: ${item.candidate_name || t('dashboard.activity.deletedCandidate', 'Deleted Candidate')}`,
      }),
      timestamp: item.created_at || new Date().toISOString(),
    }))

    // LLM usage history activities - invalid candidates
    const invalidCandidateActivities: ActivityItem[] = invalidCandidates.map((item: any, idx: number) => ({
      id: `invalid-candidate-${item.id || idx}`,
      type: 'candidate_updated',
      description: t('dashboard.activity.candidateInvalid', {
        name: item.candidate_name || t('dashboard.activity.invalidCandidate', 'Invalid Candidate'),
        defaultValue: `Invalid: ${item.candidate_name || t('dashboard.activity.invalidCandidate', 'Invalid Candidate')}`,
      }),
      timestamp: item.created_at || new Date().toISOString(),
    }))

    // Combine all activities and sort by timestamp
    const allActivities = [...regularActivities, ...deletedCandidateActivities, ...invalidCandidateActivities]
      .sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime())
      .slice(0, 5)

    return allActivities
  }, [candidates, deletedCandidates, invalidCandidates, t])

  const offerActivities: ActivityItem[] = useMemo(() => {
    // Regular offer activities
    const regularActivities: ActivityItem[] = offers
      .filter(offer => offer.updated_at || offer.created_date)
      .slice(0, 10)
      .map(offer => {
        const rawStatus = (offer.status || '').toString().toLowerCase()
        const statusLabel = rawStatus
          ? t(`dashboard.activity.offerStatus.${rawStatus}`, { defaultValue: rawStatus.replace(/_/g, ' ') })
          : t('dashboard.activity.unknownStatus')
        return {
          id: `offer-${offer.id}`,
          type: 'offer_updated',
          description: t('dashboard.activity.offerUpdated', {
            title: offer.job_title || offer.title || t('dashboard.activity.untitledOffer'),
            status: statusLabel,
          }),
          timestamp: offer.updated_at || offer.created_date || new Date().toISOString(),
        }
      })

    // LLM usage history activities - deleted offers
    const deletedOfferActivities: ActivityItem[] = deletedOffers.map((item: any, idx: number) => ({
      id: `deleted-offer-${item.id || idx}`,
      type: 'offer_updated',
      description: t('dashboard.activity.offerDeleted', {
        title: item.offer_title || t('dashboard.activity.deletedOffer', 'Deleted Offer'),
        defaultValue: `Deleted: ${item.offer_title || t('dashboard.activity.deletedOffer', 'Deleted Offer')}`,
      }),
      timestamp: item.created_at || new Date().toISOString(),
    }))

    // LLM usage history activities - modified offers
    const modifiedOfferActivities: ActivityItem[] = modifiedOffers.map((item: any, idx: number) => ({
      id: `modified-offer-${item.id || idx}`,
      type: 'offer_updated',
      description: t('dashboard.activity.offerModified', {
        title: item.offer_title || t('dashboard.activity.modifiedOffer', 'Modified Offer'),
        defaultValue: `Modified: ${item.offer_title || t('dashboard.activity.modifiedOffer', 'Modified Offer')}`,
      }),
      timestamp: item.created_at || new Date().toISOString(),
    }))

    // Combine all activities and sort by timestamp
    const allActivities = [...regularActivities, ...deletedOfferActivities, ...modifiedOfferActivities]
      .sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime())
      .slice(0, 5)

    return allActivities
  }, [offers, deletedOffers, modifiedOffers, t])

  const timeRangeLabel = t(`organizations.stats.timeRanges.${timeRange}`, timeRange)

  return (
    <div className="w-full max-w-full min-w-0 space-y-6">
      <DashboardHeader timeRange={timeRange} onTimeRangeChange={setTimeRange} />

      <DashboardRecruitmentStats
        analyzedCvCount={analyzedCvCount}
        offersCount={offersCount}
        matchingCount={matchingCount}
        manualMatchingTimeMinutes={manualMatchingTimeMinutes}
        timeSavedMinutes={timeSavedMinutes}
        interviewTimeSavedMinutes={interviewTimeSavedMinutes}
        interviewRetainedPercent={interviewAudioPercent}
        hiredCandidatesCount={hiredCandidatesCount}
        aiInterviewCount={aiInterviewCount}
        footerLabel={timeRangeLabel}
      />

      <div className="grid w-full min-w-0 grid-cols-1 items-stretch gap-3 lg:grid-cols-2 lg:gap-2">
        <SkillsRadarChart
          variant="radar"
          data={categoryAverages}
          totalResults={categoryAveragesCount}
          footerLabel={timeRangeLabel}
          offers={radarOffers}
          selectedOfferId={selectedRadarOfferId}
          onOfferChange={setSelectedRadarOfferId}
        />
        <TimeSavedChart data={timeSavedChartData} interviewHistory={interviewTimeSavedHistory} />
      </div>

      <div className="grid w-full min-w-0 grid-cols-1 gap-3 lg:grid-cols-2 lg:gap-2">
        <SkillsRadarChart
          variant="scatter"
          data={categoryAverages}
          scatterData={candidateScorePoints}
          scatterTotal={candidateScoreTotal}
          footerLabel={timeRangeLabel}
          offers={radarOffers}
          selectedOfferId={selectedScatterOfferId}
          onOfferChange={setSelectedScatterOfferId}
        />
        <DashboardRecentOffersCard
          offers={recentOffersStats}
          footerLabel={timeRangeLabel}
        />
      </div>

      {/* Temporarily hidden: organization selector (restore when needed)
      {user?.is_superuser && organizationsList.length > 0 && (
        <div className="flex items-center gap-4">
          <label htmlFor="organization-filter" className="text-sm font-medium">
            {t('organizations.selectOrganization', 'Select Organization')}:
          </label>
          <select
            id="organization-filter"
            value={selectedOrgId === null ? 'superuser' : selectedOrgId}
            onChange={(e) => {
              const value = e.target.value
              if (value === 'superuser') {
                setSelectedOrgId(null)
              } else {
                const orgId = parseInt(value, 10)
                if (!isNaN(orgId)) {
                  setSelectedOrgId(orgId)
                }
              }
            }}
            className="px-3 py-2 text-sm border rounded-md bg-white dark:bg-gray-950 border-input"
          >
            <option value="superuser">{t('dashboard.superuser', 'Superuser Dashboard')}</option>
            {organizationsList.map((org) => (
              <option key={org.id} value={org.id}>
                {org.name}
              </option>
            ))}
          </select>
        </div>
      )}
      */}

      {/* Temporarily hidden old stats/cards in new dashboard */}
      {false && (selectedOrgId || (selectedOrgId === null && user?.organization)) && (
        <ConsumptionStats
          organizationId={
            (selectedOrgId ??
              (user?.organization == null
                ? undefined
                : typeof user.organization === 'number'
                  ? user.organization
                  : (user.organization as Organization).id)) as number | undefined
          }
          timeRange={timeRange}
          candidateActivities={candidateActivities}
          offerActivities={offerActivities}
          loadingActivity={loading}
        />
      )}

      {loading && (
        <div className="flex items-center justify-center h-32">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      )}
    </div>
  )
}
